import {
  RATING_UOMS,
  isAggregatorCargoType,
  parsePropertyGroups,
  type CargoDefinition,
  type CargoDefinitionProductPayload,
  type CargoDefinitionVariant,
  type ProductQuantityUnitValue,
  type CargoDefinitionVariantPayload,
  type CargoPropertyGroupsPayload,
  type CargoTypeValue,
  type RatingUomValue,
} from '@my/tms-api';

/**
 * Editor draft shape — the new (variants + property groups) model.
 *
 * <p>
 * Every primitive field is required so the editor never branches on
 * "undefined or null". Variant + custom-variant lists default to <c>[]</c>.
 * The Display Name defaults to the cargo type's label (operator can
 * override) so a +Add row is immediately savable for any non-aggregator
 * type with at least one variant ticked.
 * </p>
 *
 * <p>
 * Lives in its own file so the editor component stays a pure component
 * export — Vite's react-refresh plugin requires that for HMR to work.
 * </p>
 */
export interface CargoDefinitionDraft {
  /** Empty for a brand-new draft, populated when editing an existing row. */
  id: string | null;
  cargoType: CargoTypeValue;
  displayName: string;

  /** Catalogue keys (e.g. `trailer.standard_curtainsider`). */
  equipmentKeys: string[];
  unitsOfMeasure: RatingUomValue[];
  serviceTagIds: string[];

  /**
   * Preset variant ids ticked from the global catalogue (e.g. `"euro"`,
   * `"iso-40"`). Empty for aggregator types.
   */
  variantPresetIds: string[];

  /**
   * Operator-defined custom variants — free name + per-type fields. Each
   * row carries a local id (`cv-${Date.now()}`) for stable React keys; the
   * id isn't persisted (the BE assigns a Guid on save).
   */
  customVariants: CustomVariantDraft[];

  /** Property-groups payload — see `cargoPropertyGroups.ts` for the shape. */
  propertyGroups: CargoPropertyGroupsPayload;

  /**
   * The "Products" chip. Off → orders of this type carry no product. On →
   * none, one or more products linked (none linked = any of the
   * organisation's products at order entry).
   */
  hasProducts: boolean;

  /** Linked products, in picker order. */
  products: CargoProductDraft[];

  /** Default accounting category code; `''` = none. */
  accountingCategoryCode: string;

  /** VAT rate (tax schema id) on the standard line; `''` = the VAT treatment's. */
  taxRateId: string;
}

/** One linked product in the editor. Quantity is text while typing. */
export interface CargoProductDraft {
  productId: string;
  productName: string;
  /** Decimal comma accepted; parsed on save. */
  quantity: string;
  /** `''` = no unit chosen. */
  unit: ProductQuantityUnitValue | '';
  /** This product's category on this cargo type; `''` = inherit the definition's. */
  accountingCategoryCode: string;
  /** This product's VAT rate on this cargo type; `''` = inherit the definition's. */
  taxRateId: string;
}

/**
 * A custom variant on the draft — per-type fields are nullable; the editor
 * renders only the subset relevant to the parent's cargo type.
 */
export interface CustomVariantDraft {
  /** Stable React key for this row — never sent to the BE. */
  localId: string;
  name: string;

  // Pallet
  loadingMetres: number | null;
  // Iso Tank / Container
  perTrailer: number | null;
  // Livestock
  maxTransitHours: number | null;
  // Pharmaceutical
  minTempC: number | null;
  maxTempC: number | null;
  // Other / dimensional
  lengthCm: number | null;
  widthCm: number | null;
  heightCm: number | null;
  tareWeightKg: number | null;
}

export function emptyCustomVariant(): CustomVariantDraft {
  return {
    localId: `cv-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
    name: '',
    loadingMetres: null,
    perTrailer: null,
    maxTransitHours: null,
    minTempC: null,
    maxTempC: null,
    lengthCm: null,
    widthCm: null,
    heightCm: null,
    tareWeightKg: null,
  };
}

/**
 * Default starting draft. Defaults to Pallet because that's the most
 * common starting type; Display Name mirrors the cargo type label until
 * the operator types something custom.
 */
export function emptyDraft(): CargoDefinitionDraft {
  return {
    id: null,
    cargoType: 'Pallet',
    displayName: 'Pallet',
    equipmentKeys: [],
    unitsOfMeasure: [],
    serviceTagIds: [],
    variantPresetIds: [],
    customVariants: [],
    propertyGroups: {},
    hasProducts: false,
    products: [],
    accountingCategoryCode: '',
    taxRateId: '',
  };
}

/** Hydrate a draft from a saved row coming back from the BE. */
export function toDraft(row: CargoDefinition): CargoDefinitionDraft {
  const variantPresetIds: string[] = [];
  const customVariants: CustomVariantDraft[] = [];
  for (const v of row.variants) {
    if (v.presetKey) {
      variantPresetIds.push(v.presetKey);
    } else {
      customVariants.push(toCustomVariantDraft(v));
    }
  }
  return {
    id: row.id,
    cargoType: row.cargoType,
    displayName: row.displayName,
    equipmentKeys: [...row.equipmentCatalogueKeys],
    unitsOfMeasure: parseUomString(row.unitsOfMeasure),
    serviceTagIds: [...row.serviceTagIds],
    variantPresetIds,
    customVariants,
    propertyGroups: parsePropertyGroups(row.propertyGroupsJson),
    hasProducts: row.hasProducts ?? false,
    products: (row.products ?? []).map((p) => ({
      productId: p.productId,
      productName: p.productName,
      quantity: p.defaultQuantity == null ? '' : String(p.defaultQuantity),
      unit: p.quantityUnit ?? '',
      accountingCategoryCode: p.accountingCategoryCode ?? '',
      taxRateId: p.taxRateId ?? '',
    })),
    accountingCategoryCode: row.accountingCategoryCode ?? '',
    taxRateId: row.taxRateId ?? '',
  };
}

/** `""` → null; `"24,5"` / `"24.5"` → 24.5; anything else → NaN. */
export function parseQuantity(text: string): number | null {
  const trimmed = text.trim();
  if (!trimmed) return null;
  return Number(trimmed.replace(',', '.'));
}

/** Per linked product: a quantity that is not a positive number, or one with no unit. */
export function productRowErrors(p: CargoProductDraft): { quantity: boolean; unit: boolean } {
  const qty = parseQuantity(p.quantity);
  return {
    quantity: qty !== null && (!Number.isFinite(qty) || qty <= 0),
    unit: qty !== null && p.unit === '',
  };
}

export function productsAreValid(draft: CargoDefinitionDraft): boolean {
  if (!draft.hasProducts) return true;
  return draft.products.every((p) => {
    const e = productRowErrors(p);
    return !e.quantity && !e.unit;
  });
}

/**
 * The chip + list → the wire. The BE refuses a list with the chip off, so the
 * list is only sent with it on.
 */
export function buildProductsPayload(draft: CargoDefinitionDraft): {
  hasProducts: boolean;
  products: CargoDefinitionProductPayload[];
} {
  if (!draft.hasProducts) return { hasProducts: false, products: [] };
  return {
    hasProducts: true,
    products: draft.products.map((p) => ({
      productId: p.productId,
      productName: p.productName,
      defaultQuantity: parseQuantity(p.quantity),
      quantityUnit: p.unit === '' ? null : p.unit,
      accountingCategoryCode: p.accountingCategoryCode || null,
      taxRateId: p.taxRateId || null,
    })),
  };
}

function toCustomVariantDraft(v: CargoDefinitionVariant): CustomVariantDraft {
  return {
    localId: `cv-${v.id}`,
    name: v.name,
    loadingMetres: v.loadingMetres,
    perTrailer: v.perTrailer,
    maxTransitHours: v.maxTransitHours,
    minTempC: v.minTempC,
    maxTempC: v.maxTempC,
    lengthCm: v.lengthCm,
    widthCm: v.widthCm,
    heightCm: v.heightCm,
    tareWeightKg: v.tareWeightKg,
  };
}

/**
 * Build the variants payload sent to the BE on Create / Update. Preset
 * picks come first (in catalogue order, preserved by the operator's
 * selection), customs come after — sortOrder reflects this so the BE
 * stores a stable order.
 */
export function buildVariantsPayload(
  draft: CargoDefinitionDraft,
  catalogueByType: (type: CargoTypeValue) => Array<{ id: string; name: string }>,
): CargoDefinitionVariantPayload[] {
  if (isAggregatorCargoType(draft.cargoType)) return [];

  const out: CargoDefinitionVariantPayload[] = [];
  const presets = catalogueByType(draft.cargoType);
  let sort = 0;

  for (const id of draft.variantPresetIds) {
    const preset = presets.find((p) => p.id === id);
    if (!preset) continue; // stale preset id — drop
    out.push({
      presetKey: preset.id,
      // Snapshot the name so a future catalogue rename doesn't silently
      // mutate this row's display.
      name: preset.name,
      sortOrder: sort++,
    });
  }

  for (const c of draft.customVariants) {
    const name = c.name.trim();
    if (!name) continue; // skip incomplete customs — validator would reject anyway
    out.push({
      presetKey: null,
      name,
      sortOrder: sort++,
      loadingMetres: c.loadingMetres,
      perTrailer: c.perTrailer,
      maxTransitHours: c.maxTransitHours,
      minTempC: c.minTempC,
      maxTempC: c.maxTempC,
      lengthCm: c.lengthCm,
      widthCm: c.widthCm,
      heightCm: c.heightCm,
      tareWeightKg: c.tareWeightKg,
    });
  }

  return out;
}

/**
 * Parse the BE's comma-joined `unitsOfMeasure` into a typed list. Defends
 * against unknown tokens — the BE rejects them on save so seeing one here
 * almost always means the FE enum is stale; log + drop rather than crash.
 */
function parseUomString(raw: string): RatingUomValue[] {
  if (!raw) return [];
  const known = new Set<string>(RATING_UOMS);
  const out: RatingUomValue[] = [];
  for (const token of raw.split(',')) {
    const t = token.trim();
    if (!t) continue;
    if (known.has(t)) {
      out.push(t as RatingUomValue);
    } else {
      console.warn(`[CargoDefinitions] Unknown UoM "${t}" — dropping.`);
    }
  }
  return out;
}

/** Emit UoMs in canonical (declaration) order — matches the BE canonicaliser. */
export function orderedUomString(uoms: RatingUomValue[]): string {
  const set = new Set(uoms);
  return RATING_UOMS.filter((u) => set.has(u)).join(',');
}
